import { useCallback, useEffect, useState } from "react";
import {
  getSavedListings,
  listSessionSavedListings,
  removeSavedListing,
  removeSessionSavedListing,
  saveListing,
} from "../../clientViewRuntime/api/publicClientViewApi";
import { mapSdkError } from "../../clientViewRuntime/api/sdkClient";
import { useClientView } from "../../clientViewRuntime/context/ClientViewProvider";
import { usePublicAccount } from "../../clientViewRuntime/context/PublicAccountProvider";
import { useLocale } from "../../intl";
import {
  BUYER_LIKES_CHANGED_EVENT,
  emitGuestLikePrompt,
  isVehicleLiked,
} from "../../utils/automap/buyerLikesStorage";
import { removeVehicleFromCompare } from "../../utils/automap/buyerCompareStorage";
import { trackVehicleSaved, trackVehicleUnsaved } from "../../utils/automap/vehicleLikeAnalytics";

async function resolveSavedListingRecord(slug, listingId, { isAuthenticated, visitorId }) {
  const body = isAuthenticated
    ? await getSavedListings(slug)
    : await listSessionSavedListings(slug, visitorId);
  return (body.savedListings || []).find(
    (item) => (item.canonicalListingId || item.listingSnapshotJson?.id) === listingId
  );
}

export default function VehicleLikeButton({
  listing,
  className = "",
  sourceComponent = "vehicle_like_button",
  sourcePage = null,
  compact = true,
}) {
  const { slug, visitorId, sessionId, isPreview } = useClientView();
  const { t } = useLocale();
  const {
    isAuthenticated,
    isListingSaved,
    markListingSaved,
    markListingUnsaved,
    refreshSavedState,
    showFlash,
  } = usePublicAccount();
  const [isBusy, setIsBusy] = useState(false);
  const [, setMirrorTick] = useState(0);

  const listingId = listing?.id || listing?.vehicleListingId;
  const liked = listingId ? isListingSaved(listingId) || isVehicleLiked(slug, listingId) : false;

  useEffect(() => {
    const onChanged = (event) => {
      if (event.detail?.slug === slug) {
        setMirrorTick((value) => value + 1);
      }
    };
    window.addEventListener(BUYER_LIKES_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(BUYER_LIKES_CHANGED_EVENT, onChanged);
  }, [slug]);

  const syncUnlike = useCallback(async () => {
    const match = await resolveSavedListingRecord(slug, listingId, {
      isAuthenticated,
      visitorId,
    });
    if (!match?.publicUserSavedListingId) return;
    if (isAuthenticated) {
      await removeSavedListing(slug, match.publicUserSavedListingId);
    } else {
      await removeSessionSavedListing(slug, match.publicUserSavedListingId, visitorId);
    }
  }, [slug, listingId, isAuthenticated, visitorId]);

  const handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (isPreview || !listingId || isBusy) return;

    const wasLiked = liked;
    setIsBusy(true);

    if (wasLiked) {
      markListingUnsaved(listingId);
      removeVehicleFromCompare(slug, listingId);
    } else {
      markListingSaved(listingId);
    }

    try {
      if (wasLiked) {
        if (isAuthenticated) {
          await syncUnlike();
        }
        trackVehicleUnsaved(listing, { sourceComponent, sourcePage });
        showFlash(t("automotive.likes.removedFlash"));
      } else {
        try {
          await saveListing(
            slug,
            { listingId, visitorId, sessionId },
            { anonymous: !isAuthenticated }
          );
        } catch (apiError) {
          if (isAuthenticated) {
            showFlash(
              mapSdkError(apiError).message || t("automotive.likes.errorFlash"),
              "error"
            );
          }
        }
        trackVehicleSaved(listing, { sourceComponent, sourcePage });
        showFlash(t("automotive.likes.savedFlash"));
        if (!isAuthenticated) {
          emitGuestLikePrompt({ slug, listingId });
        }
      }
      await refreshSavedState();
    } catch (error) {
      if (wasLiked) {
        markListingSaved(listingId);
      } else {
        markListingUnsaved(listingId);
      }
      const mapped = mapSdkError(error);
      showFlash(mapped.message || t("automotive.likes.errorFlash"), "error");
    } finally {
      setIsBusy(false);
    }
  };

  if (isPreview || !listingId) {
    return null;
  }

  const saveLabel = t("automotive.likes.saveVehicle");
  const savedLabel = t("automotive.likes.removeSavedVehicle");
  const label = liked ? savedLabel : saveLabel;

  const buttonClassName = [
    "vehicle-like-btn",
    compact ? "vehicle-like-btn--compact" : "",
    className,
    liked ? "vehicle-like-btn--liked" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type="button"
      className={buttonClassName}
      onClick={handleClick}
      disabled={isBusy}
      aria-pressed={liked}
      aria-label={label}
      aria-busy={isBusy || undefined}
      title={label}
    >
      <span className="vehicle-like-btn__icon" aria-hidden="true">
        {liked ? "♥" : "♡"}
      </span>
      <span className="vehicle-like-btn__label">{label}</span>
    </button>
  );
}
